'use client';

import { useEffect, useState } from 'react';
import { RotateCcw, Download } from 'lucide-react';

const STORAGE_KEY = 'planetroadmap.nps-calculator.v1';

type ScoreCounts = {
  // 0..10 buckets (count per score)
  counts: number[]; // length 11
};

function defaults(): ScoreCounts {
  // Realistic shape: most responses 7-9
  return {
    counts: [1, 1, 2, 2, 3, 5, 8, 25, 40, 22, 18],
  };
}

function classify(i: number): 'detractor' | 'passive' | 'promoter' {
  if (i <= 6) return 'detractor';
  if (i <= 8) return 'passive';
  return 'promoter';
}

function verdict(nps: number): { label: string; tone: 'good' | 'ok' | 'warn' } {
  if (nps >= 50) return { label: 'Excellent — top quartile', tone: 'good' };
  if (nps >= 30) return { label: 'Great — above-average', tone: 'good' };
  if (nps >= 0) return { label: 'OK — middle of the pack', tone: 'ok' };
  return { label: 'Negative — more detractors than promoters', tone: 'warn' };
}

export function NpsCalculator() {
  const [data, setData] = useState<ScoreCounts>({ counts: Array(11).fill(0) });
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as ScoreCounts;
        if (parsed && Array.isArray(parsed.counts) && parsed.counts.length === 11) {
          setData(parsed);
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setData(defaults());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [data, hydrated]);

  function update(idx: number, value: number) {
    setData((curr) => ({
      counts: curr.counts.map((c, i) => (i === idx ? Math.max(0, value) : c)),
    }));
  }

  function reset() {
    if (!confirm('Clear all responses?')) return;
    setData({ counts: Array(11).fill(0) });
  }

  function loadDefaults() {
    setData(defaults());
  }

  const total = data.counts.reduce((a, b) => a + b, 0);
  let detractors = 0;
  let passives = 0;
  let promoters = 0;
  data.counts.forEach((c, i) => {
    const cls = classify(i);
    if (cls === 'detractor') detractors += c;
    else if (cls === 'passive') passives += c;
    else promoters += c;
  });
  const promoterPct = total ? (promoters / total) * 100 : 0;
  const passivePct = total ? (passives / total) * 100 : 0;
  const detractorPct = total ? (detractors / total) * 100 : 0;
  const nps = total ? Math.round(promoterPct - detractorPct) : 0;
  const v = verdict(nps);

  function exportCsv() {
    const rows = [
      ['Score', 'Bucket', 'Count'],
      ...data.counts.map((c, i) => [i, classify(i), c]),
      ['', '', ''],
      ['Total responses', '', total],
      ['Promoters', '', promoters],
      ['Passives', '', passives],
      ['Detractors', '', detractors],
      ['NPS', '', nps],
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nps.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading calculator…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Responses" value={String(total)} />
        <StatCard label="Promoters" value={`${promoters} (${promoterPct.toFixed(0)}%)`} />
        <StatCard label="Detractors" value={`${detractors} (${detractorPct.toFixed(0)}%)`} />
        <StatCard
          label="NPS"
          value={total ? String(nps) : '—'}
          accent={v.tone === 'good'}
          warn={v.tone === 'warn'}
        />
      </div>

      {total > 0 && (
        <div
          className={`rounded-2xl p-5 text-sm border ${
            v.tone === 'good'
              ? 'bg-primary/5 border-primary/20'
              : v.tone === 'warn'
                ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/40'
                : 'bg-muted/30 border-border/60'
          }`}
        >
          <span className="font-semibold">{v.label}.</span>{' '}
          <span className="text-muted-foreground">
            NPS = % promoters − % detractors. Passives don&apos;t count toward the score, but they do indicate
            risk of churn if a competitor offers a better experience.
          </span>
        </div>
      )}

      {/* Stacked bar */}
      {total > 0 && (
        <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-6">
          <div className="text-xs font-semibold text-muted-foreground mb-3">Response mix</div>
          <div className="flex h-8 rounded-full overflow-hidden">
            <div
              className="bg-red-500"
              style={{ width: `${detractorPct}%` }}
              title={`Detractors: ${detractorPct.toFixed(0)}%`}
            />
            <div
              className="bg-amber-400"
              style={{ width: `${passivePct}%` }}
              title={`Passives: ${passivePct.toFixed(0)}%`}
            />
            <div
              className="bg-emerald-500"
              style={{ width: `${promoterPct}%` }}
              title={`Promoters: ${promoterPct.toFixed(0)}%`}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-2">
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500" /> Detractors (0–6)
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400" /> Passives (7–8)
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> Promoters (9–10)
            </span>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-6">
        <h3 className="text-sm font-semibold mb-4">Enter response count for each score (0–10)</h3>
        <div className="grid grid-cols-3 sm:grid-cols-6 md:grid-cols-11 gap-2">
          {data.counts.map((c, i) => {
            const cls = classify(i);
            const colorClass =
              cls === 'detractor'
                ? 'border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/10'
                : cls === 'passive'
                  ? 'border-amber-200 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-950/10'
                  : 'border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-950/10';
            return (
              <label
                key={i}
                className={`rounded-xl border p-2 text-center transition-colors ${colorClass}`}
              >
                <div className="text-xs font-mono font-semibold text-muted-foreground">{i}</div>
                <input
                  type="number"
                  min={0}
                  value={c}
                  onChange={(e) => update(i, Number(e.target.value))}
                  className="w-full text-center mt-1 bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded"
                />
              </label>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <button
          onClick={loadDefaults}
          className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
        >
          Load sample
        </button>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={total === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center pt-2">
        Saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}

function StatCard({
  label,
  value,
  accent,
  warn,
}: {
  label: string;
  value: string;
  accent?: boolean;
  warn?: boolean;
}) {
  let cls = 'bg-white dark:bg-card border border-border/60';
  if (accent && !warn) cls = 'bg-primary/5 border border-primary/20';
  if (warn) cls = 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40';
  return (
    <div className={`rounded-2xl p-4 ${cls}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={`text-2xl font-bold mt-1 ${
          accent && !warn ? 'text-primary' : ''
        } ${warn ? 'text-amber-700 dark:text-amber-400' : ''}`}
      >
        {value}
      </div>
    </div>
  );
}
